import { Avatar, AvatarGradientFallback, Checkbox, Heading, Input, OrgAvatar, Text } from "@athena/ui";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";

import type { OrganizationsQuery } from "../../graphql/organizations.generated";
import {
    UsersDocument,
    UsersCountDocument,
    UserIdsDocument,
    type UserIdsQuery,
    type UsersQuery,
} from "../../graphql/users.generated";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { DEFAULT_PAGE_SIZE, useWindowedRows } from "@/hooks/useWindowedRows";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

interface StepAudienceProps {
    orgs: OrgRow[];
    selectedOrgs: Set<string>;
    setSelectedOrgs: React.Dispatch<React.SetStateAction<Set<string>>>;
    selectedPeople: Set<string>;
    setSelectedPeople: React.Dispatch<React.SetStateAction<Set<string>>>;
}

type AudienceTab = "org" | "users";

type OrgRow = OrganizationsQuery["organizations"][number];
type UserRow = UsersQuery["users"][number];

/** Sentinel for "every organization" — `Select` has no value for an absent choice. */
const ALL = "__all__";

/** `MAX_LIMIT` in `common/database/repositories/_pagination.ts` — the largest page the API honours. */
const SELECT_ALL_PAGE_SIZE = 200;

/**
 * Pages fetched at once while collecting ids.
 *
 * "Select all" means every match, and the platform's largest is every user on it — 422 pages at
 * the API's 200-row cap. Sequentially that is a minute of a disabled button; six at a time brings
 * it under ten seconds without pretending the browser should open 422 sockets.
 */
const SELECT_ALL_CONCURRENCY = 6;

export const StepAudience = ({
    orgs,
    selectedOrgs,
    setSelectedOrgs,
    selectedPeople,
    setSelectedPeople,
}: StepAudienceProps) => {
    const client = useApolloClient();
    const [
        tab,
        setTab,
    ] = useState<AudienceTab>("org");
    const [
        expandedOrgs,
        setExpandedOrgs,
    ] = useState<Set<string>>(new Set());
    const [
        orgFilter,
        setOrgFilter,
    ] = useState(ALL);
    const [
        search,
        setSearch,
    ] = useState("");
    const [
        orgSearch,
        setOrgSearch,
    ] = useState("");

    const toggleOrg = (id: string) =>
        setSelectedOrgs((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const togglePerson = (id: string) =>
        setSelectedPeople((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleExpanded = (id: string) =>
        setExpandedOrgs((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    /**
     * **The people list is filtered by the server, not in the browser.**
     *
     * `users` returns at most 50 rows without an explicit page, and this step used to be handed
     * one such page and filter it in memory — so searching a name matched against fifty of
     * 85,371 users and confidently reported "No users match your filters" for everyone else.
     * `UserFilterInput` searches email and both names; `organizationId` narrows to a membership.
     * The users page moved to the same shape for the same reason.
     */
    const settledSearch = useDebouncedValue(search);
    /** Debounced while typing, immediate when emptied — clearing is a decision, not a keystroke. */
    const queryTerm = search.trim() === "" ? "" : settledSearch.trim();
    const filter = useMemo(
        () => ({
            ...(queryTerm ? { search: queryTerm } : {}),
            ...(orgFilter !== ALL ? { organizationId: orgFilter } : {}),
        }),
        [
            queryTerm,
            orgFilter,
        ],
    );

    /*
     * A window onto the matches, grown by scrolling, rather than the whole set: the filter can
     * still be wide enough to select every user on the platform, and fetching that to render a
     * checkbox list is the failure mode `useWindowedRows` exists to avoid.
     */
    const {
        rows: people,
        loading: peopleLoading,
        loadingMore,
        hasMore,
        loadMore,
    } = useWindowedRows<UserRow>(UsersDocument, (d) => (d as UsersQuery | undefined)?.users, {
        variables: { filter },
    });
    /*
     * Scroll or pages, the reader's choice, portal-wide — see `useListView`. Safe for a picker
     * because a selection is a set of ids and "Select all" already means every match rather than
     * every loaded row, so which rows are on screen never changes what is selected.
     */
    const view = useListView(people, { hasMore, loadMore, pageSize: DEFAULT_PAGE_SIZE });

    const sentinelRef = useInfiniteScroll(loadMore, hasMore);

    // How many people actually match — the loaded rows are a window and cannot count past
    // themselves, and this is the number "Select all" promises to act on.
    const { data: countData } = useQuery(UsersCountDocument, { variables: { filter } });
    const matchCount = countData?.usersCount;

    const filteredOrgs = orgs.filter((o) => o.name.toLowerCase().includes(orgSearch.toLowerCase()));
    const allOrgsSelected = filteredOrgs.length > 0 && filteredOrgs.every((o) => selectedOrgs.has(o.id));
    const selectAllOrgs = () =>
        setSelectedOrgs((prev) => {
            const next = new Set(prev);
            filteredOrgs.forEach((o) => next.add(o.id));
            return next;
        });
    const deselectAllOrgs = () =>
        setSelectedOrgs((prev) => {
            const next = new Set(prev);
            filteredOrgs.forEach((o) => next.delete(o.id));
            return next;
        });

    /*
     * "Select all" means every match, not every row that has scrolled into view.
     *
     * Against a window those are different sets, and the difference is silent: the operator sees
     * "Select all 3,412 users", gets the 100 they had scrolled past, and the assignment goes out
     * to a fraction of the audience with nothing on screen saying so. So this pages the query
     * itself rather than reading the list's state.
     *
     * Straight through the client, and never from the cache: the cached list is the window this
     * step is rendering, and both reading it back and writing these pages into it would defeat
     * the point of holding a window at all.
     */
    const [
        selectingAll,
        setSelectingAll,
    ] = useState<boolean>(false);
    /** Ids collected so far, so a run measured in seconds is not a motionless "Working…". */
    const [
        selectProgress,
        setSelectProgress,
    ] = useState<number>(0);

    const fetchIdPage = async (offset: number): Promise<string[]> => {
        const { data } = await client.query<UserIdsQuery>({
            // Ids only. The list's own query carries each user's memberships and their
            // organizations, which is several kilobytes a row — the reason collecting every id
            // used to need a ceiling rather than just time.
            query: UserIdsDocument,
            variables: { filter, limit: SELECT_ALL_PAGE_SIZE, offset },
            fetchPolicy: "no-cache",
        });
        return (data?.users ?? []).map((u) => u.id);
    };

    /**
     * Every id the filter matches — all of them, however many that is.
     *
     * `usersCount` has already been asked the same question, so the number of pages is known
     * rather than discovered: they are fetched a few at a time instead of one after another, and
     * the loop ends because the total said so. A count that arrives late (or not at all) falls
     * back to walking until a short page comes back, which is the same answer more slowly.
     */
    const matchingIds = async (): Promise<string[]> => {
        const offsets: number[] = [];
        if (matchCount != null) {
            for (let offset = 0; offset < matchCount; offset += SELECT_ALL_PAGE_SIZE) offsets.push(offset);
        }

        if (offsets.length === 0) {
            const ids: string[] = [];
            for (let offset = 0; ; offset += SELECT_ALL_PAGE_SIZE) {
                const page = await fetchIdPage(offset);
                ids.push(...page);
                setSelectProgress(ids.length);
                if (page.length < SELECT_ALL_PAGE_SIZE) return ids;
            }
        }

        const ids: string[] = [];
        for (let i = 0; i < offsets.length; i += SELECT_ALL_CONCURRENCY) {
            const pages = await Promise.all(offsets.slice(i, i + SELECT_ALL_CONCURRENCY).map(fetchIdPage));
            for (const page of pages) ids.push(...page);
            setSelectProgress(ids.length);
        }
        return ids;
    };

    const applyToAllMatches = (apply: (next: Set<string>, ids: string[]) => void) => () => {
        setSelectingAll(true);
        setSelectProgress(0);
        void (async () => {
            try {
                const ids = await matchingIds();
                setSelectedPeople((prev) => {
                    const next = new Set(prev);
                    apply(next, ids);
                    return next;
                });
            } finally {
                setSelectingAll(false);
            }
        })();
    };

    const selectAll = applyToAllMatches((next, ids) => ids.forEach((id) => next.add(id)));
    const deselectAll = applyToAllMatches((next, ids) => ids.forEach((id) => next.delete(id)));

    /*
     * Which way the toggle points is decided by the loaded rows, because whether the *unloaded*
     * matches are selected cannot be known without fetching them — and a button label is not
     * worth a round trip. The count beside it is still the real total, so the button says what
     * pressing it will do.
     */
    const allLoadedSelected = people.length > 0 && people.every((p) => selectedPeople.has(p.id));
    const matchLabel = matchCount ?? people.length;

    /*
     * "All" means all, so the label says so.
     *
     * It briefly did not: collecting ids paged the list's own query, which carries each user's
     * memberships and organizations, so a ceiling of 50,000 stood between the button and the
     * ~85,000 users an empty filter matches — and the label had to name two numbers to stay
     * honest. An ids-only query made the fetch cheap enough that the ceiling came out instead.
     */
    const selectAllLabel = `Select all ${matchLabel.toLocaleString()} users`;
    const deselectAllLabel = `Deselect all ${matchLabel.toLocaleString()} users`;

    return (
        <div className="flex flex-col gap-5">
            <div>
                <Heading level={3}>Who should get it?</Heading>
                <Text muted className="text-sm">
                    Assign to organizations and chapters, or pick individual people — or both.
                </Text>
            </div>

            <div className="bg-muted inline-flex items-center gap-1 self-start rounded-lg p-1">
                {(
                    [
                        "org",
                        "users",
                    ] as AudienceTab[]
                ).map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`rounded-md px-4 py-1.5 text-sm transition-colors ${
                            tab === t
                                ? "bg-background text-foreground font-semibold shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {t === "org" ? "By organization" : "By users"}
                    </button>
                ))}
            </div>

            <div className="h-180 overflow-hidden">
                <div className="flex h-full flex-col" hidden={tab !== "org"}>
                    <div className="mb-3 flex shrink-0 gap-3">
                        <div className="relative flex-1">
                            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                            <Input
                                placeholder="Search organizations..."
                                className="h-10 pl-9 text-sm"
                                value={orgSearch}
                                onChange={(e) => setOrgSearch(e.target.value)}
                                type="text"
                            />
                        </div>
                    </div>
                    <div className="mb-3 flex shrink-0 items-center justify-between">
                        <Text weight="semibold" className="text-sm">
                            Select orgs{" "}
                            <span className="text-muted-foreground font-normal">({selectedOrgs.size} selected)</span>
                        </Text>
                        <button
                            onClick={allOrgsSelected ? deselectAllOrgs : selectAllOrgs}
                            className="text-sm font-semibold text-sky-500 hover:text-sky-600"
                        >
                            {allOrgsSelected
                                ? `Deselect all ${filteredOrgs.length} orgs`
                                : `Select all ${filteredOrgs.length} orgs`}
                        </button>
                    </div>
                    <div className="border-border divide-border min-h-0 flex-1 divide-y overflow-y-auto rounded-xl border">
                        <div className="bg-muted/50 border-border flex items-center gap-4 border-b px-4 py-2">
                            <Checkbox
                                checked={allOrgsSelected}
                                onCheckedChange={allOrgsSelected ? deselectAllOrgs : selectAllOrgs}
                                className="shrink-0"
                            />
                            <Text muted className="flex-1 text-xs font-semibold tracking-wide uppercase">
                                Organization
                            </Text>
                        </div>
                        {filteredOrgs.map((org) => {
                            const isExpanded = expandedOrgs.has(org.id);
                            return (
                                <div key={org.id}>
                                    <div className="flex items-center gap-4 px-4 py-3">
                                        <Checkbox
                                            checked={selectedOrgs.has(org.id)}
                                            onCheckedChange={() => toggleOrg(org.id)}
                                            className="shrink-0"
                                        />
                                        <OrgAvatar
                                            id={org.id}
                                            fallback={org.name
                                                .split(" ")
                                                .slice(0, 2)
                                                .map((w) => w[0])
                                                .join("")
                                                .toUpperCase()
                                                .slice(0, 2)}
                                            size="sm"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <Text weight="semibold" className="text-sm">
                                                {org.name}
                                            </Text>
                                            <Text muted className="text-xs">
                                                {org.memberCount.toLocaleString()} students ·{" "}
                                                {org.subOrganizations.length} sub-orgs
                                            </Text>
                                        </div>
                                        {org.subOrganizations.length > 0 && (
                                            <button
                                                onClick={() => toggleExpanded(org.id)}
                                                className="border-border hover:bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-colors"
                                            >
                                                {isExpanded ? (
                                                    <ChevronDown className="text-muted-foreground h-4 w-4" />
                                                ) : (
                                                    <ChevronRight className="text-muted-foreground h-4 w-4" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {isExpanded && (
                                        <div className="bg-muted/30 border-border divide-border divide-y border-t">
                                            {org.subOrganizations.map((sub) => (
                                                <div key={sub.id} className="flex items-center gap-4 py-2.5 pr-4 pl-14">
                                                    <Checkbox
                                                        checked={selectedOrgs.has(sub.id)}
                                                        onCheckedChange={() => toggleOrg(sub.id)}
                                                        className="shrink-0"
                                                    />
                                                    <Text className="flex-1 text-sm">{sub.name}</Text>
                                                    <Text muted className="text-sm tabular-nums">
                                                        {sub.memberCount}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex h-full flex-col" hidden={tab !== "users"}>
                    <div className="flex h-full flex-col gap-3">
                        <div className="flex shrink-0 gap-3">
                            <div className="relative flex-1">
                                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    placeholder="Search by name, email or organization..."
                                    className="h-10 pl-9 text-sm"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    type="text"
                                />
                            </div>
                            <OrganizationSelect
                                value={orgFilter}
                                onValueChange={setOrgFilter}
                                className="h-10 flex-1 text-sm"
                                aria-label="Filter by organization"
                                leadingOptions={[
                                    { value: ALL, label: "All organizations" },
                                ]}
                            />
                        </div>
                        <div className="flex shrink-0 items-center justify-between">
                            <Text weight="semibold" className="text-sm">
                                Select people{" "}
                                <span className="text-muted-foreground font-normal">
                                    ({selectedPeople.size.toLocaleString()} selected)
                                </span>
                            </Text>
                            <button
                                onClick={allLoadedSelected ? deselectAll : selectAll}
                                disabled={selectingAll || matchLabel === 0}
                                className="text-sm font-semibold text-sky-500 hover:text-sky-600 disabled:opacity-50"
                            >
                                {selectingAll
                                    ? selectProgress > 0
                                        ? `Selecting ${selectProgress.toLocaleString()} of ${matchLabel.toLocaleString()}…`
                                        : "Working…"
                                    : allLoadedSelected
                                      ? deselectAllLabel
                                      : selectAllLabel}
                            </button>
                        </div>
                        <div className="border-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border">
                            <div className="bg-muted/50 border-border flex shrink-0 items-center gap-4 border-b px-4 py-2">
                                <Checkbox
                                    checked={allLoadedSelected}
                                    onCheckedChange={allLoadedSelected ? deselectAll : selectAll}
                                    disabled={selectingAll}
                                    className="shrink-0"
                                />
                                <Text muted className="flex-1 text-xs font-semibold tracking-wide uppercase">
                                    User
                                </Text>
                                <Text muted className="w-56 shrink-0 text-xs font-semibold tracking-wide uppercase">
                                    Organization
                                </Text>
                            </div>
                            <div className="border-border border-b px-3 py-2">
                                <ListViewControls view={view} noun="users" />
                            </div>
                            <div className="divide-border min-h-0 flex-1 divide-y overflow-y-auto">
                                {peopleLoading ? (
                                    <div className="py-10 text-center">
                                        <Text muted className="text-sm">
                                            Loading users…
                                        </Text>
                                    </div>
                                ) : view.rows.length === 0 ? (
                                    <div className="py-10 text-center">
                                        <Text muted className="text-sm">
                                            No users match your filters.
                                        </Text>
                                    </div>
                                ) : (
                                    view.rows.map((person) => {
                                        const fullName = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
                                        const initials =
                                            `${person.firstName?.[0] ?? ""}${person.lastName?.[0] ?? ""}`.toUpperCase();
                                        const orgName = person.memberships[0]?.organization.name ?? "";
                                        return (
                                            <div key={person.id} className="flex items-center gap-4 px-4 py-3">
                                                <Checkbox
                                                    checked={selectedPeople.has(person.id)}
                                                    onCheckedChange={() => togglePerson(person.id)}
                                                    className="shrink-0"
                                                />
                                                <Avatar size="sm" className="shrink-0">
                                                    <AvatarGradientFallback>{initials}</AvatarGradientFallback>
                                                </Avatar>
                                                <div className="min-w-0 flex-1">
                                                    <Text weight="semibold" className="truncate text-sm">
                                                        {person.email}
                                                    </Text>
                                                    <Text muted className="truncate text-xs">
                                                        {fullName}
                                                    </Text>
                                                </div>
                                                <Text className="w-56 shrink-0 text-sm">{orgName}</Text>
                                            </div>
                                        );
                                    })
                                )}
                                {/* Approaching this asks for the next page — see `useInfiniteScroll`. */}
                                {view.mode === "scroll" && <div ref={sentinelRef} />}
                                {loadingMore && (
                                    <div className="py-4 text-center">
                                        <Text muted className="text-sm">
                                            Loading more…
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
